import React, { useState, useEffect, useRef } from 'react';
import { Outlet, Link, useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { config } from '../config';
import './role-navigation.css';
import { Bars3Icon, XMarkIcon } from '@heroicons/react/24/outline';

export default function TechnicienLayout() {

    const navigate = useNavigate();
    const location = useLocation();
    const [nonLus, setNonLus] = useState(0);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const tabsRef = useRef(null);
    const isCatalogue = location.pathname.startsWith('/technicien/gestion-applications');

    const user = JSON.parse(localStorage.getItem('user') || '{}');
    const API_BASE = config.apiBase;

    const handleLogout = () => {
        localStorage.clear();
        toast.success('Déconnexion réussie');
        navigate('/login');
    };

    const checkMessages = async () => {
        try {
            const token = localStorage.getItem('token');
            if (!token) return;
            
            const response = await fetch(`${API_BASE}/get_messages_techniques.php`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            const data = await response.json();
            if (data.success) {
                setNonLus(data.non_lus);
            }
        } catch (error) {
            console.error('Erreur chargement messages:', error);
        }
    };

    useEffect(() => {
        checkMessages();
        const interval = setInterval(checkMessages, 30000);
        
        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };
        window.addEventListener('scroll', handleScroll);
        
        return () => {
            clearInterval(interval);
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    useEffect(() => {
        if (tabsRef.current) {
            const activeTab = tabsRef.current.querySelector('.active-tab');
            if (activeTab) {
                activeTab.scrollIntoView({ 
                    behavior: 'smooth', 
                    block: 'nearest', 
                    inline: 'center' 
                });
            }
        }
    }, [location.pathname]);

    const navLinks = [
        { path: '/securite', name: 'Connexion & empreinte', icon: '↗' },
        // === ESPACE TECHNICIEN ===
        { path: '/technicien', name: 'Dashboard', icon: '📊' },
        { path: '/technicien/gestion', name: 'TVSahel', icon: '📺' },
        { path: '/technicien/recharges', name: 'Recharges', icon: '💰' },
        { path: '/technicien/statistiques', name: 'Statistiques', icon: '📈' },
        { path: '/technicien/moyens-paiement', name: 'Moyens de paiement', icon: '💳' },
        { path: '/technicien/nettoyage-posts', name: 'Nettoyage posts', icon: '🧹' },
        { path: '/technicien/gestion-etudiants', name: 'Gestion étudiants', icon: '👥' },
        { path: '/technicien/gestion-applications', name: 'Applications', icon: '📱' },
        { path: '/technicien/envoyer-message', name: 'Envoyer message', icon: '📢' },

        // === MAIRIE ===
        { path: '/mairie', name: '🏛️ Accueil Mairie', icon: '🏛️' },
        { path: '/mairie/recensement', name: '📝 Recensement', icon: '📝' },
        { path: '/mairie/collecteurs', name: '👥 Collecteurs', icon: '👥' },
        { path: '/mairie/paiement', name: '💰 Paiement', icon: '💰' },
        { path: '/mairie/liste', name: '📋 Liste', icon: '📋' },
        { path: '/mairie/taxes', name: '🏷️ Taxes', icon: '🏷️' },
        { path: '/mairie/statistiques', name: '📊 Statistiques', icon: '📊' },
        { path: '/mairie/stats-collecteurs', name: '📊 Collecteurs', icon: '📊' },
        // ✅ NOUVEAU LIEN POUR LA GESTION DES TECHNICIENS
        { path: '/mairie/techniciens', name: '👨‍💻 Techniciens', icon: '👨‍💻' },
    ];

    const isActive = (path) => {
        if (path === '/technicien' && location.pathname === '/technicien') return true;
        if (path === '/mairie' && location.pathname === '/mairie') return true;
        if (path !== '/technicien' && path !== '/mairie' && location.pathname.startsWith(path)) return true;
        return false;
    };

    // Regrouper les liens par catégorie
    const techLinks = navLinks.filter(link => link.path.startsWith('/technicien') || link.path === '/securite');
    const mairieLinks = navLinks.filter(link => link.path.startsWith('/mairie'));

    return (
        <div className="technician-shell min-h-screen bg-gradient-to-br from-slate-50 via-slate-100 to-slate-200">
            
            {/* Navbar */}
            <nav className={`technician-navbar fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled 
                    ? 'bg-slate-900/95 backdrop-blur-lg shadow-2xl' 
                    : 'bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900'
            }`}>
                <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
                    
                    {/* Première ligne: Logo et infos utilisateur */}
                    <div className="technician-header-row flex h-16 items-center justify-between border-b border-white/10">
                        <div className="technician-brand flex items-center gap-3">
                            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 shadow-lg">
                                <span className="text-xl">🛠️</span>
                            </div>
                            <div>
                                <h1 className="text-xl font-bold text-white tracking-tight">
                                    Espace Technicien
                                </h1>
                                <p className="text-xs text-slate-400">NDTV • Support Technique</p>
                            </div>
                        </div>

                        <div className="technician-header-actions flex items-center gap-4">
                            <Link
                                to="/messages-techniques"
                                className="technician-message-link relative rounded-xl bg-white/5 px-3 py-2 text-sm font-medium text-slate-300 transition hover:bg-white/10 hover:text-white"
                            >
                                <span className="flex items-center gap-2">
                                    💬 Messages
                                    {nonLus > 0 && (
                                        <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-xs font-bold text-white shadow-lg animate-pulse">
                                            {nonLus > 9 ? '9+' : nonLus}
                                        </span>
                                    )}
                                </span>
                            </Link>

                            <div className="technician-user flex items-center gap-3 pl-4">
                                <div className="flex h-8 w-8 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600">
                                    <span className="text-sm">👤</span>
                                </div>
                                <div className="text-sm hidden sm:block">
                                    <p className="font-medium text-white">{user.prenom} {user.nom}</p>
                                    <p className="text-xs text-slate-400">Technicien</p>
                                </div>
                                <button
                                    onClick={handleLogout}
                                    className="rounded-xl bg-red-500/20 px-3 py-1.5 text-sm font-medium text-red-300 transition hover:bg-red-500 hover:text-white"
                                >
                                    Déconnexion
                                </button>
                            </div>

                            <button
                                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                                aria-label="Ouvrir le menu technicien" aria-expanded={mobileMenuOpen} aria-controls="technician-mobile-menu" className="role-menu-trigger lg:hidden text-white"
                            >
                                {mobileMenuOpen ? <XMarkIcon aria-hidden="true" /> : <Bars3Icon aria-hidden="true" />}
                            </button>
                        </div>
                    </div>

                    {/* Deuxième ligne: Barre d'onglets horizontale avec défilement */}
                    <div className="relative">
                        <div 
                            ref={tabsRef}
                            className="flex overflow-x-auto scrollbar-hide py-2 gap-1"
                            style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
                        >
                            {/* Liens Espace Technicien */}
                            {techLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`tab-item whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        isActive(link.path)
                                            ? 'bg-cyan-500/20 text-cyan-400 active-tab'
                                            : 'text-slate-400 hover:text-white hover:bg-white/5'
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        <span>{link.icon}</span>
                                        <span>{link.name}</span>
                                    </span>
                                </Link>
                            ))}

                            {/* Séparateur */}
                            <div className="w-px h-8 bg-white/10 mx-2"></div>

                            {/* Liens Mairie */}
                            {mairieLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    className={`tab-item whitespace-nowrap px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 ${
                                        isActive(link.path)
                                            ? 'bg-emerald-500/20 text-emerald-400 active-tab'
                                            : 'text-slate-400 hover:text-white hover:bg-white/5'
                                    }`}
                                >
                                    <span className="flex items-center gap-2">
                                        <span>{link.icon}</span>
                                        <span>{link.name}</span>
                                    </span>
                                </Link>
                            ))}
                        </div>
                        
                        <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-slate-900 to-transparent pointer-events-none"></div>
                        <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-slate-900 to-transparent pointer-events-none"></div>
                    </div>
                </div>
            </nav>

            {/* Mobile Menu Overlay */}
            {mobileMenuOpen && (
                <>
                    <div 
                        className="fixed inset-0 z-40 bg-black/60 backdrop-blur-sm lg:hidden"
                        onClick={() => setMobileMenuOpen(false)}
                    />
                    <div id="technician-mobile-menu" className="technician-mobile-menu fixed left-0 right-0 top-24 z-40 mx-4 overflow-hidden rounded-2xl bg-slate-800 shadow-2xl lg:hidden animate-slide-down">
                        <div className="max-h-[calc(100vh-7rem)] overflow-y-auto p-2">
                            {/* Espace Technicien */}
                            <div className="text-xs text-slate-500 px-4 py-2 font-semibold uppercase tracking-wider">Espace Technicien</div>
                            {techLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                                        isActive(link.path)
                                            ? 'bg-gradient-to-r from-cyan-500 to-blue-600 text-white shadow-lg'
                                            : 'text-slate-300 hover:bg-white/5 hover:text-white'
                                    }`}
                                >
                                    <span className="text-xl">{link.icon}</span>
                                    <span>{link.name}</span>
                                    {isActive(link.path) && (
                                        <span className="ml-auto text-xs">✓</span>
                                    )}
                                </Link>
                            ))}

                            <div className="my-2 h-px bg-white/10"></div>

                            {/* Mairie */}
                            <div className="text-xs text-slate-500 px-4 py-2 font-semibold uppercase tracking-wider">🏛️ Mairie</div>
                            {mairieLinks.map((link) => (
                                <Link
                                    key={link.path}
                                    to={link.path}
                                    onClick={() => setMobileMenuOpen(false)}
                                    className={`flex items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all ${
                                        isActive(link.path)
                                            ? 'bg-gradient-to-r from-emerald-500 to-green-600 text-white shadow-lg'
                                            : 'text-slate-300 hover:bg-white/5 hover:text-white'
                                    }`}
                                >
                                    <span className="text-xl">{link.icon}</span>
                                    <span>{link.name}</span>
                                    {isActive(link.path) && (
                                        <span className="ml-auto text-xs">✓</span>
                                    )}
                                </Link>
                            ))}

                            <div className="my-2 h-px bg-white/10"></div>
                            
                            <Link
                                to="/messages-techniques"
                                onClick={() => setMobileMenuOpen(false)}
                                className="flex items-center justify-between rounded-xl px-4 py-3 text-sm font-medium text-slate-300 transition hover:bg-white/5 hover:text-white"
                            >
                                <div className="flex items-center gap-3">
                                    <span className="text-xl">💬</span>
                                    <span>Messages techniques</span>
                                </div>
                                {nonLus > 0 && (
                                    <span className="flex h-5 w-5 items-center justify-center rounded-full bg-gradient-to-r from-red-500 to-pink-500 text-xs font-bold text-white">
                                        {nonLus > 9 ? '9+' : nonLus}
                                    </span>
                                )}
                            </Link>

                            <div className="my-2 h-px bg-white/10"></div>
                            <div className="flex items-center gap-3 rounded-xl px-4 py-3">
                                <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gradient-to-br from-cyan-500 to-blue-600">
                                    <span className="text-sm">👤</span>
                                </div>
                                <div className="flex-1">
                                    <p className="font-medium text-white">{user.prenom} {user.nom}</p>
                                    <p className="text-xs text-slate-400">Technicien</p>
                                </div>
                                <button
                                    onClick={() => {
                                        handleLogout();
                                        setMobileMenuOpen(false);
                                    }}
                                    className="rounded-xl bg-red-500/20 px-3 py-2 text-sm font-medium text-red-300 transition hover:bg-red-500 hover:text-white"
                                >
                                    Déconnexion
                                </button>
                            </div>
                        </div>
                    </div>
                </>
            )}

            <div className="h-32"></div>

            <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
                <div className={isCatalogue ? '' : 'rounded-2xl bg-white shadow-xl ring-1 ring-black/5'}>
                    <div className={isCatalogue ? '' : 'p-4 sm:p-6'}>
                        <Outlet />
                    </div>
                </div>
            </main>

            <style jsx>{`
                @keyframes slide-down {
                    from {
                        opacity: 0;
                        transform: translateY(-20px);
                    }
                    to {
                        opacity: 1;
                        transform: translateY(0);
                    }
                }
                .animate-slide-down {
                    animation: slide-down 0.2s ease-out;
                }
                .scrollbar-hide::-webkit-scrollbar {
                    display: none;
                }
                .tab-item {
                    transition: all 0.2s ease;
                }
                .tab-item:hover {
                    transform: translateY(-1px);
                }
                .active-tab {
                    box-shadow: 0 0 10px rgba(6, 182, 212, 0.3);
                }
            `}</style>
        </div>
    );
}
